import Navbar from "@/components/Navbar";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { ArrowLeft, FileText, Download } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

const CostScheduleEstimation = () => {
  const navigate = useNavigate();
  
  const handleViewDetails = (title: string) => {
    navigate(`/agency-solutions?search=${encodeURIComponent(title)}`);
  };

  const knowledgeBases = [
    {
      title: "Material & Cost Optimization: Contract Specifications",
      description: "Advanced tools for optimizing material costs and developing comprehensive contract specifications for federal projects",
      type: "Optimization Tool",
      lastUpdated: "2024-01-20"
    },
    {
      title: "Procurement & Bid Support: Vendor Analysis",
      description: "Comprehensive vendor analysis and bid support systems for federal procurement processes",
      type: "Support System",
      lastUpdated: "2024-02-16"
    },
    {
      title: "Schedule Scenarios: Resource Loaded Planning",
      description: "Resource-loaded planning tools for developing and analyzing multiple schedule scenarios in federal projects",
      type: "Planning Tool",
      lastUpdated: "2024-01-28"
    },
    {
      title: "Rough Order of Magnitude Costing",
      description: "ROM costing frameworks and methodologies for early-stage federal project cost estimation",
      type: "Estimation Tool",
      lastUpdated: "2024-02-12"
    },
    {
      title: "Phased Project Execution Timeline",
      description: "Comprehensive frameworks for developing phased project execution timelines for complex federal construction projects",
      type: "Framework",
      lastUpdated: "2024-01-25"
    }
  ];

  return (
    <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-6">
          <Link to="/gsa-channel" className="inline-flex items-center text-primary hover:text-primary/80 mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to GSA Channel
          </Link>
          <h1 className="text-3xl font-bold mb-2">Cost and Schedule Estimation</h1>
          <p className="text-muted-foreground">
            Comprehensive tools and frameworks for cost optimization, procurement support, schedule planning, and project execution in federal construction.
          </p>
        </div>

        <div className="grid gap-6">
          {knowledgeBases.map((kb, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-xl mb-2">{kb.title}</CardTitle>
                    <p className="text-muted-foreground">{kb.description}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      {kb.type}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      Updated: {kb.lastUpdated}
                    </span>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3">
                  <Button variant="outline" size="sm" onClick={() => handleViewDetails(kb.title)}>
                    <FileText className="h-4 w-4 mr-2" />
                    View Details
                  </Button>
                  <Button variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
};

export default CostScheduleEstimation;